import React, { useState, useEffect } from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
  Link,
} from "react-router-dom";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";
import ShopHero from "../../components/ShopHero";
import { formatCurrency } from "../../utils/displayHelpers";
import "./ProfessionalSelection.css";

const ProfessionalSelection = () => {
  const { businessSlug, locationSlug } = useParams();
  const [searchParams] = useSearchParams();
  const serviceId = searchParams.get("serviceId");
  const serviceName = searchParams.get("serviceName");
  const navigate = useNavigate();

  const [shop, setShop] = useState(null);
  const [service, setService] = useState(null);
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [searchQuery, setSearchQuery] = useState("");
  // The hero offers "Shop Catalogue" only when the shop actually sells products.
  const [hasProducts, setHasProducts] = useState(false);
  const [sortBy, setSortBy] = useState("name");

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (serviceName || serviceId) {
      fetchProfessionalsData();
    } else {
      setError("Service name or ID is required");
      setLoading(false);
    }
  }, [businessSlug, locationSlug, serviceId, serviceName]);

  const fetchProfessionalsData = async () => {
    try {
      setLoading(true);

      // Fetch shop data
      const shopUrl = `${process.env.REACT_APP_API_BASE_URL}/api/business/${businessSlug}/${locationSlug}`;
      const shopRes = await fetch(shopUrl);
      if (!shopRes.ok) throw new Error("Shop location not found");
      const shopData = await shopRes.json();
      setShop(shopData);

      // Does this shop sell products? The hero offers "Shop Catalogue" only if it does, matching the
      // shop landing page.
      try {
        const productsRes = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/v2/products/shop/${shopData._id}?featured=true&limit=1`,
        );
        if (productsRes.ok) {
          const productsData = await productsRes.json();
          const list = productsData.products || productsData || [];
          setHasProducts(Array.isArray(list) && list.length > 0);
        }
      } catch (productErr) {
        // A product lookup must never stop a customer choosing a professional.
      }

      // Use new endpoint if serviceName is provided (from deduplicated landing page)
      if (serviceName) {
        const profUrl = `${process.env.REACT_APP_API_BASE_URL}/api/services/by-name/${businessSlug}/${locationSlug}/${encodeURIComponent(serviceName)}/professionals`;
        const profRes = await fetch(profUrl);
        if (!profRes.ok) throw new Error("Service not found");
        const profData = await profRes.json();
        setService(profData.service);
        setProfessionals(profData.professionals || []);
      } else {
        // Legacy path: use serviceId (for backwards compatibility)
        const serviceRes = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/services/${serviceId}`,
        );
        if (!serviceRes.ok) throw new Error("Service not found");
        const serviceData = await serviceRes.json();
        setService(serviceData);

        const staffRes = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/services/${serviceId}/available-staff?shopId=${shopData._id}`,
        );
        if (!staffRes.ok) throw new Error("Failed to load professionals");
        const staffData = await staffRes.json();
        setProfessionals(staffData.availableStaff || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBookNow = (staffId, profServiceId, professionalRole) => {
    // Use the professional's specific serviceId from their service record
    const finalServiceId = profServiceId || serviceId;
    const resolvedShopId = shop?._id || shop?.id || shop?.slug || locationSlug;

    // R7 booth renters and R4 freelancers use the same independent booking flow
    const isIndependent =
      professionalRole === "R7" || professionalRole === "R4";

    if (isIndependent) {
      // Independent professional booking - uses FreelancerBooking component
      // This ensures R7/R4 manage their own bookings independently
      navigate(
        `/freelancer-booking?staffId=${staffId}&serviceId=${finalServiceId}`,
      );
    } else {
      // Business/employee booking (R1/R3) - uses CustomerBooking component
      navigate(
        `/booking?shopId=${resolvedShopId}&staffId=${staffId}&serviceId=${finalServiceId}`,
      );
    }
  };

  if (loading) {
    return (
      <>
        <CustomerHeader />
        <div
          style={{
            padding: "60px 20px",
            textAlign: "center",
            minHeight: "60vh",
          }}
        >
          <div
            style={{ fontSize: isMobile ? "12px" : "16px", color: "#6b7280" }}
          >
            Loading professionals...
          </div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  if (error || !shop || !service) {
    return (
      <>
        <CustomerHeader />
        <div
          style={{
            padding: "60px 20px",
            textAlign: "center",
            minHeight: "60vh",
          }}
        >
          <div
            style={{
              fontSize: isMobile ? "12px" : "16px",
              color: "#ef4444",
              marginBottom: "20px",
            }}
          >
            {error || "Information not found"}
          </div>
          <button
            onClick={() =>
              navigate(`/business/${businessSlug}/${locationSlug}/services`)
            }
            style={{
              padding: "12px 24px",
              background: "#2d6da3",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: isMobile ? "11px" : "14px",
              fontWeight: "600",
            }}
          >
            Back to Services
          </button>
        </div>
        <CustomerFooter />
      </>
    );
  }

  return (
    <>
      <CustomerHeader />
      <div className="professional-selection-container">
        {/* Same hero as the shop, services and packages pages. */}
        <ShopHero
          name={shop.name}
          locationLine={[shop.city, shop.state].filter(Boolean).join(", ")}
          contactLine={`📞 ${shop.phone || ""}${
            shop.email ? ` • ✉️ ${shop.email}` : ""
          }`}
          logoUrl={shop.business?.logo?.url || shop.logoUrl}
          backgroundImage={shop.images?.[0]?.url || shop.images?.[0]?.path}
          schedule={shop.schedule}
          isMobile={isMobile}
          actions={[
            {
              label: "Back to Services",
              onClick: () =>
                navigate(`/business/${businessSlug}/${locationSlug}/services`),
              variant: "solid",
            },
            ...(hasProducts
              ? [
                  {
                    label: "Shop Catalogue",
                    onClick: () => navigate(`/shop/${locationSlug}/products`),
                    variant: "ghost",
                  },
                ]
              : []),
          ]}
        />

        {/* Which service the customer is choosing a professional for. */}
        <div className="ps-context">
          <p className="ps-context-eyebrow">Choosing a professional for</p>
          <h1 className="ps-context-title">{service.name}</h1>
          {service.description && (
            <p className="ps-context-desc">{service.description}</p>
          )}
        </div>

        {/* Content */}
        <div
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            padding: isMobile ? "16px 16px" : "24px 24px",
          }}
        >
          <h3
            style={{
              fontWeight: "600",
              marginBottom: "8px",
              color: "#111827",
              textAlign: "center",
              margin: "0 0 8px 0",
              fontSize: isMobile ? "13px" : "15px",
            }}
          >
            Choose Your Professional
          </h3>
          <p
            style={{
              fontSize: isMobile ? "11px" : "13px",
              color: "#6b7280",
              textAlign: "center",
              marginBottom: "24px",
            }}
          >
            Select a professional who offers this service
          </p>

          {/* Search & Sort - Only show if 5+ professionals */}
          {professionals.length >= 5 && (
            <div
              style={{
                marginBottom: "24px",
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: "12px",
                alignItems: isMobile ? "stretch" : "center",
              }}
            >
              <input
                type="text"
                placeholder="🔍 Search professionals..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  flex: 1,
                  padding: "10px 14px",
                  fontSize: "12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  outline: "none",
                }}
              />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  padding: "10px 14px",
                  fontSize: "12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  outline: "none",
                  background: "white",
                  cursor: "pointer",
                  minWidth: isMobile ? "auto" : "180px",
                }}
              >
                <option value="name">Sort by Name</option>
                <option value="rating">Sort by Rating</option>
              </select>
            </div>
          )}

          {/* Professionals Grid */}
          {(() => {
            // Filter professionals by search
            let filteredProfessionals = professionals.filter((prof) => {
              const fullName =
                `${prof.firstName} ${prof.lastName}`.toLowerCase();
              const type = (prof.professionalType || "").toLowerCase();
              return (
                fullName.includes(searchQuery.toLowerCase()) ||
                type.includes(searchQuery.toLowerCase())
              );
            });

            // Sort professionals
            if (sortBy === "name") {
              filteredProfessionals.sort((a, b) =>
                `${a.firstName} ${a.lastName}`.localeCompare(
                  `${b.firstName} ${b.lastName}`,
                ),
              );
            } else if (sortBy === "rating") {
              filteredProfessionals.sort(
                (a, b) => (b.rating || 0) - (a.rating || 0),
              );
            }

            if (filteredProfessionals.length === 0) {
              return (
                <div
                  style={{
                    textAlign: "center",
                    padding: "60px 20px",
                    background: "#f9fafb",
                    borderRadius: "12px",
                  }}
                >
                  <div style={{ fontSize: "48px", marginBottom: "16px" }}>
                    🔍
                  </div>
                  <h3
                    style={{
                      fontSize: "14px",
                      color: "#374151",
                      marginBottom: "8px",
                      fontWeight: "700",
                    }}
                  >
                    {professionals.length === 0
                      ? "No professionals available"
                      : "No matches found"}
                  </h3>
                  <p
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      marginBottom: "24px",
                    }}
                  >
                    {professionals.length === 0
                      ? "This service is currently not offered at this location"
                      : "Try adjusting your search"}
                  </p>
                  {professionals.length === 0 && (
                    <button
                      onClick={() =>
                        navigate(
                          `/business/${businessSlug}/${locationSlug}/services`,
                        )
                      }
                      style={{
                        padding: "12px 24px",
                        background: "#2d6da3",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: "600",
                      }}
                    >
                      Choose Another Service
                    </button>
                  )}
                </div>
              );
            }

            return (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "repeat(2, 1fr)"
                    : "repeat(auto-fill, minmax(180px, 1fr))",
                  gap: isMobile ? "16px" : "20px",
                }}
              >
                {filteredProfessionals.map((professional) => (
                  <div
                    key={professional._id}
                    style={{
                      background: "white",
                      borderRadius: "12px",
                      padding: "0",
                      border: "1px solid #e5e7eb",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      transition: "all 0.3s",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      cursor: "pointer",
                      height: isMobile ? "236px" : "220px",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.boxShadow =
                        "0 8px 16px rgba(0,0,0,0.15)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 2px 8px rgba(0,0,0,0.1)";
                    }}
                  >
                    {/* Professional Photo */}
                    {professional.headshotImage ? (
                      <img
                        src={professional.headshotImage}
                        alt={`${professional.firstName} ${professional.lastName}`}
                        style={{
                          width: "70px",
                          height: "70px",
                          borderRadius: "50%",
                          objectFit: "cover",
                          border: "3px solid #e5e7eb",
                          marginBottom: "0",
                          marginTop: isMobile ? "10px" : "12px",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "70px",
                          height: "70px",
                          borderRadius: "50%",
                          background:
                            "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "24px",
                          fontWeight: "700",
                          marginBottom: "0",
                          marginTop: isMobile ? "10px" : "12px",
                          border: "3px solid #e5e7eb",
                        }}
                      >
                        {professional.firstName?.[0]}
                        {professional.lastName?.[0]}
                      </div>
                    )}

                    {/* Professional Name */}
                    <h3
                      style={{
                        fontSize: "13px",
                        fontWeight: "700",
                        margin: isMobile ? "8px 0 4px 0" : "10px 0 4px 0",
                        marginBottom: "4px",
                        color: "#111827",
                        lineHeight: "1.3",
                      }}
                    >
                      {professional.firstName} {professional.lastName}
                    </h3>

                    {/* Service Price and Duration */}
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#2d6da3",
                        fontWeight: "600",
                        marginBottom: isMobile ? "2px" : "4px",
                      }}
                    >
                      ${professional.price?.toFixed(2) || "0.00"} •{" "}
                      {professional.durationMinutes || 0} min
                    </div>

                    {/* Buttons */}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                        width: "70%",
                        marginTop: isMobile ? "2px" : "4px",
                        padding: "0",
                      }}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const isIndependent = ["R4", "R7"].includes(
                            professional.role,
                          );
                          const independentBasePath =
                            professional.role === "R7"
                              ? "/freelancers"
                              : "/freelancer";

                          if (isIndependent && professional.slug) {
                            navigate(`${independentBasePath}/${professional.slug}`);
                            return;
                          }

                          if (isIndependent) {
                            navigate(`${independentBasePath}/${professional._id}`);
                            return;
                          }

                          if (professional.slug) {
                            navigate(
                              `/business/${businessSlug}/${locationSlug}/professional/${professional.slug}`,
                            );
                          } else {
                            navigate(`/professional/${professional._id}`);
                          }
                        }}
                        style={{
                          padding: "8px 12px",
                          background: "#fff",
                          color: "#2d6da3",
                          border: "1px solid #2d6da3",
                          borderRadius: "8px",
                          fontSize: "11px",
                          fontWeight: "600",
                          cursor: "pointer",
                          transition: "all 0.2s",
                          width: "100%",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#f0f7ff";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "#fff";
                        }}
                      >
                        View Profile
                      </button>
                      <button
                        onClick={() =>
                          handleBookNow(
                            professional._id,
                            professional.serviceId,
                            professional.role,
                          )
                        }
                        style={{
                          padding: "10px 12px",
                          background:
                            "linear-gradient(135deg, #f59e0b 0%, #f97316 100%)",
                          color: "white",
                          border: "none",
                          borderRadius: "8px",
                          fontSize: "12px",
                          fontWeight: "600",
                          cursor: "pointer",
                          transition: "all 0.2s",
                          width: "100%",
                          boxShadow: "0 2px 4px rgba(245, 158, 11, 0.3)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.boxShadow =
                            "0 4px 8px rgba(245, 158, 11, 0.5)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow =
                            "0 2px 4px rgba(245, 158, 11, 0.3)";
                        }}
                      >
                        📅 Book Now
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Results Count */}
          {professionals.length > 0 && (
            <div
              style={{
                textAlign: "center",
                marginTop: "32px",
                color: "#6b7280",
                fontSize: "12px",
              }}
            >
              Showing{" "}
              {
                professionals.filter((prof) => {
                  const fullName =
                    `${prof.firstName} ${prof.lastName}`.toLowerCase();
                  const type = (prof.professionalType || "").toLowerCase();
                  return (
                    fullName.includes(searchQuery.toLowerCase()) ||
                    type.includes(searchQuery.toLowerCase())
                  );
                }).length
              }{" "}
              of {professionals.length}{" "}
              {professionals.length === 1 ? "professional" : "professionals"}
            </div>
          )}
        </div>
      </div>
      <CustomerFooter />
    </>
  );
};

export default ProfessionalSelection;
